import type { CSSProperties, FC, ReactNode } from 'react'
import type { Faq } from '@/types'

import { useId, useState } from 'react'
import { Plus } from 'lucide-react'
import { cn } from '@/lib/utils'

interface FaqListProps {
    faqs: Array<Faq>
    showCategory?: boolean
    defaultOpen?: number
}

export const FaqList: FC<FaqListProps> = ({ faqs, showCategory = true, defaultOpen = 0 }): ReactNode => {
    const baseId = useId().replace(/[^a-zA-Z0-9_-]/g, '')
    const [open, setOpen] = useState<Set<number>>(() => new Set(defaultOpen >= 0 ? [defaultOpen] : []))

    const toggle = (index: number) => {
        setOpen(current => {
            const next = new Set(current)

            if (next.has(index)) next.delete(index)
            else next.add(index)

            return next
        })
    }

    return (
        <ul className='border-t border-burgundy/10'>
            {faqs.map((faq, index) => {
                const expanded = open.has(index)
                const buttonId = `${baseId}-question-${index}`
                const panelId = `${baseId}-answer-${index}`

                return (
                    <li
                        key={faq.id}
                        data-reveal=''
                        className='border-b border-burgundy/10'
                        style={{ '--reveal-delay': `${Math.min(index, 6) * 50}ms` } as CSSProperties}
                    >
                        <h3>
                            <button
                                id={buttonId}
                                type='button'
                                aria-expanded={expanded}
                                aria-controls={panelId}
                                onClick={() => toggle(index)}
                                className='group flex w-full items-start gap-6 py-6 text-left'
                            >
                                <span className='flex-1'>
                                    {showCategory && faq.category && (
                                        <span className='mb-2 block font-mono text-[10.5px] font-medium uppercase tracking-[0.18em] text-stone'>
                                            {faq.category}
                                        </span>
                                    )}

                                    <span className='block text-lg font-medium leading-snug text-burgundy transition-colors duration-300 group-hover:text-burgundy-muted md:text-xl'>
                                        {faq.question}
                                    </span>
                                </span>

                                <span
                                    aria-hidden='true'
                                    className={cn(
                                        'mt-1 flex size-9 shrink-0 items-center justify-center rounded-full border transition-[rotate,background-color,border-color,color] duration-500 ease-out-expo',
                                        expanded ? 'rotate-45 border-burgundy bg-burgundy text-cream' : 'border-burgundy/20 text-burgundy group-hover:border-burgundy/45'
                                    )}
                                >
                                    <Plus className='size-4' />
                                </span>
                            </button>
                        </h3>

                        <div
                            id={panelId}
                            role='region'
                            aria-labelledby={buttonId}
                            inert={!expanded}
                            className={cn(
                                'grid transition-[grid-template-rows,opacity] duration-500 ease-out-expo',
                                expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                            )}
                        >
                            <div className='overflow-hidden'>
                                <p className='max-w-2xl pb-7 pr-12 leading-relaxed text-stone-dark'>
                                    {faq.answer}
                                </p>
                            </div>
                        </div>
                    </li>
                )
            })}
        </ul>
    )
}
